import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Contact, Search, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { MOCK_CUSTOMERS } from '../../utils/mockData';
import type { Customer } from '../../types';
import { cn } from '../../utils/cn';
// ─── Main Component ──────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 10;
export function Customers() {
  const [customers, setCustomers] = useState<Customer[]>(MOCK_CUSTOMERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };
  // Filter customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const matchesSearch = customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || customer.phone.includes(searchQuery) || customer.email.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [customers, searchQuery]);
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCustomers = filteredCustomers.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
  const startRow = (safePage - 1) * ITEMS_PER_PAGE + 1;
  const endRow = Math.min(safePage * ITEMS_PER_PAGE, filteredCustomers.length);
  return <div className="space-y-6 pb-12 animate-in fade-in duration-500" data-id="element-489">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-490">
        <div data-id="element-491">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-492">
            <Contact className="w-7 h-7 text-teal-600" data-id="element-493" />
            Customers
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-494">
            Manage customer information and view order history
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-495">
        <CardContent className="pt-6" data-id="element-496">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-497">
            <div className="relative flex-1" data-id="element-498">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-499" />
              <Input placeholder="Search by name, phone, or email..." value={searchQuery} onChange={e => handleSearch(e.target.value)} className="pl-9" data-id="element-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card data-id="element-501">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4" data-id="element-502">
          <div className="flex items-center justify-between" data-id="element-503">
            <CardTitle className="text-base font-semibold text-slate-900" data-id="element-504">
              Customer List
            </CardTitle>
            <span className="text-xs text-slate-500 font-medium" data-id="element-505">
              Showing {filteredCustomers.length} of {customers.length} customers
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-id="element-506">
          <div className="overflow-x-auto" data-id="element-507">
            <Table data-id="element-508">
              <TableHeader data-id="element-509">
                <TableRow data-id="element-510">
                  <TableHead data-id="element-511">Customer</TableHead>
                  <TableHead data-id="element-512">Phone</TableHead>
                  <TableHead className="text-right" data-id="element-513">Total Orders</TableHead>
                  <TableHead data-id="element-514">Joined Date</TableHead>
                  <TableHead data-id="element-515">Last Order</TableHead>
                  <TableHead className="text-right" data-id="element-516">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-517">
                {paginatedCustomers.length > 0 ? paginatedCustomers.map(customer => {
                const initials = customer.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                return <TableRow key={customer.id} className="hover:bg-slate-50" data-id="element-518">
                        <TableCell data-id="element-519">
                          <div className="flex items-center gap-3" data-id="element-520">
                            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xs font-bold flex-shrink-0" data-id="element-521">
                              {initials}
                            </div>
                            <div data-id="element-522">
                              <div className="font-medium text-slate-900" data-id="element-523">
                                {customer.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-524">
                                {customer.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell data-id="element-525">
                          <span className="text-sm text-slate-600" data-id="element-526">
                            {customer.phone}
                          </span>
                        </TableCell>
                        <TableCell className="text-right" data-id="element-527">
                          <span className="font-medium text-slate-900" data-id="element-528">
                            {customer.totalOrders}
                          </span>
                        </TableCell>
                        <TableCell data-id="element-529">
                          <span className="text-sm text-slate-600" data-id="element-530">
                            {customer.createdAt}
                          </span>
                        </TableCell>
                        <TableCell data-id="element-531">
                          <span className="text-sm text-slate-600" data-id="element-532">
                            {customer.lastOrderDate || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right" data-id="element-533">
                          <Button variant="ghost" size="sm" onClick={() => {}} data-id="element-534">
                            <Eye className="w-4 h-4 mr-2" data-id="element-535" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>;
              }) : <TableRow data-id="element-536">
                    <TableCell colSpan={7} className="h-32 text-center" data-id="element-537">
                      <div className="flex flex-col items-center justify-center text-slate-500" data-id="element-538">
                        <Search className="w-8 h-8 mb-2 text-slate-300" data-id="element-539" />
                        <p data-id="element-540">No customers found matching your criteria.</p>
                      </div>
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredCustomers.length > 0 && <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100" data-id="element-541">
              <p className="text-sm text-slate-500" data-id="element-542">
                Showing{' '}
                <span className="font-medium text-slate-700" data-id="element-543">{startRow}</span>{' '}
                to <span className="font-medium text-slate-700" data-id="element-544">{endRow}</span>{' '}
                of{' '}
                <span className="font-medium text-slate-700" data-id="element-545">
                  {filteredCustomers.length}
                </span>{' '}
                rows
              </p>
              <div className="flex items-center gap-2" data-id="element-546">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} data-id="element-547">
                  <ChevronLeft className="w-4 h-4 mr-1" data-id="element-548" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} data-id="element-549">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" data-id="element-550" />
                </Button>
              </div>
            </div>}
        </CardContent>
      </Card>
    </div>;
}